<template>
  <button
    type="button"
    @click="themeStore.toggleTheme()"
    class="theme-switcher"
    :title="label"
    :aria-label="label"
    :aria-pressed="themeStore.currentTheme === 'light'"
  >
    <div class="theme-switcher__icon">
      <!-- Солнце для темной темы -->
      <svg 
        v-if="themeStore.currentTheme === 'dark'"
        class="theme-switcher__sun"
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2" 
        stroke-linecap="round" 
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="5"></circle>
        <line x1="12" y1="1" x2="12" y2="3"></line>
        <line x1="12" y1="21" x2="12" y2="23"></line>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
        <line x1="1" y1="12" x2="3" y2="12"></line>
        <line x1="21" y1="12" x2="23" y2="12"></line>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
      </svg>
      
      <!-- Луна для светлой темы -->
      <svg 
        v-else
        class="theme-switcher__moon"
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2" 
        stroke-linecap="round" 
        stroke-linejoin="round"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
      </svg>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useThemeStore } from '@/stores/theme'
import { useI18n } from '@/composables/useI18n'

const themeStore = useThemeStore()
const { t } = useI18n()

const label = computed(() =>
  themeStore.currentTheme === 'dark' ? t('a11y.menu.switchToLight') : t('a11y.menu.switchToDark')
)
</script>

<style scoped>
.theme-switcher {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  /* Тот же контурный стиль, что у .a11y-trigger — кнопки стоят рядом в шапке */
  background: transparent;
  border: 1px solid var(--text-primary);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

.theme-switcher:hover {
  color: var(--primary-blue);
  border-color: var(--primary-blue);
}

.theme-switcher:active {
  transform: translateY(0);
}

.theme-switcher__icon {
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme-switcher__sun,
.theme-switcher__moon {
  transition: all var(--transition-normal);
}

.theme-switcher:hover .theme-switcher__sun,
.theme-switcher:hover .theme-switcher__moon {
  transform: rotate(15deg) scale(1.1);
}

/* Анимация при переключении */
.theme-switcher__sun {
  animation: sunRise 0.3s ease-out;
}

.theme-switcher__moon {
  animation: moonRise 0.3s ease-out;
}

@keyframes sunRise {
  from {
    opacity: 0;
    transform: rotate(-90deg) scale(0.5);
  }
  to {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
}

@keyframes moonRise {
  from {
    opacity: 0;
    transform: rotate(90deg) scale(0.5);
  }
  to {
    opacity: 1;
    transform: rotate(0deg) scale(1);
  }
}

:global(html[data-a11y-reduce-motion="true"]) .theme-switcher:hover .theme-switcher__sun,
:global(html[data-a11y-reduce-motion="true"]) .theme-switcher:hover .theme-switcher__moon {
  transform: none;
}

:global(html[data-a11y-reduce-motion="true"]) .theme-switcher__sun,
:global(html[data-a11y-reduce-motion="true"]) .theme-switcher__moon {
  animation: none;
}

/* Адаптивность */
@media (max-width: 768px) {
  .theme-switcher {
    width: 36px;
    height: 36px;
  }
  
  .theme-switcher__icon {
    width: 18px;
    height: 18px;
  }
}
</style> 